/**
 * Centralized client-side logging helper.
 *
 * Sends structured events to POST /api/log.
 * All calls are fire-and-forget: failures are silently swallowed so that
 * logging never interrupts the user flow.
 */

import { recordError } from './recentErrors';

export type ClientEventType =
  | 'page.viewed'
  | 'link.clicked'
  | 'app.error'
  | 'app.promise_rejection'
  | 'app.not_found';

interface LogContext {
  href: string;
  pathname: string;
  search: string;
  hash: string;
  userAgent: string;
  timestamp: string;
}

interface ErrorLikePayload {
  name?: string;
  message?: string;
  stack?: string;
  componentStack?: string;
  source?: string;
  status?: number;
  statusText?: string;
  details?: unknown;
}

function getContext(): LogContext {
  if (typeof window === 'undefined') {
    return {
      href: '',
      pathname: '',
      search: '',
      hash: '',
      userAgent: '',
      timestamp: new Date().toISOString(),
    };
  }

  return {
    href: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    userAgent: window.navigator.userAgent,
    timestamp: new Date().toISOString(),
  };
}

export function createErrorId(): string {
  const seed = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  return seed.toUpperCase();
}

export function logEvent(
  eventType: ClientEventType,
  payload: Record<string, unknown> = {},
): void {
  try {
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType,
        payload: {
          ...getContext(),
          ...payload,
        },
      }),
    }).catch(() => {});
  } catch {
    // Intentionally swallowed
  }
}

export function normalizeError(error: unknown): ErrorLikePayload {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'statusText' in error
  ) {
    const responseLike = error as { status?: unknown; statusText?: unknown; data?: unknown };
    return {
      status: typeof responseLike.status === 'number' ? responseLike.status : undefined,
      statusText: typeof responseLike.statusText === 'string' ? responseLike.statusText : undefined,
      details: responseLike.data,
    };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  return { details: error };
}

export function logError(
  source: 'boundary' | 'window.error' | 'window.unhandledrejection' | 'not_found',
  error: unknown,
  extras: Record<string, unknown> = {},
): string {
  const errorId = createErrorId();
  const normalized = normalizeError(error);
  const eventType = source === 'window.unhandledrejection' ? 'app.promise_rejection' : source === 'not_found' ? 'app.not_found' : 'app.error';

  // Kept for a bug report from this tab (recentErrors.ts); a missing page isn't an error there.
  if (source !== 'not_found' && typeof window !== 'undefined') {
    const text = normalized.message || normalized.statusText || (normalized.status ? `HTTP ${normalized.status}` : '') || 'Unknown error';
    recordError({ at: Date.now(), id: errorId, page: window.location.pathname, message: normalized.name ? `${normalized.name}: ${text}` : text });
  }

  logEvent(eventType, {
    errorId,
    source,
    ...normalized,
    ...extras,
  });

  if (typeof console !== 'undefined') {
    console.error(`[${errorId}] ${source}`, error, extras);
  }

  return errorId;
}
